import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { MessageCircle, Send, Clock, CheckCheck } from 'lucide-react';
import { toast } from 'sonner';
import { userAPI } from '../services/apiService';

const Messages = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState({
    subject: '',
    message: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);

    // Load messages from backend
    loadMessages();
  }, [navigate]);

  const loadMessages = async () => {
    try {
      const data = await userAPI.getMessages();
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const handleChange = (e) => {
    setNewMessage({ ...newMessage, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!newMessage.subject || !newMessage.message) {
      toast.error('Please fill all fields');
      setIsLoading(false);
      return;
    }

    try {
      await userAPI.createMessage(newMessage);
      await loadMessages();
      
      setNewMessage({ subject: '', message: '' });
      toast.success('Message sent successfully! Admin will reply soon.');
      setIsLoading(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'Unread': { className: 'bg-orange-100 text-orange-800', icon: Clock },
      'Read': { className: 'bg-blue-100 text-blue-800', icon: MessageCircle },
      'Replied': { className: 'bg-green-100 text-green-800', icon: CheckCheck }
    };
    const { className, icon: Icon } = config[status] || config['Unread'];
    return (
      <Badge className={className}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (!user) return null;

  return (
    <div className="min-h-screen py-12">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Messages & Support</h1>
              <p className="text-gray-700 text-lg font-medium">Get help from our support team</p>
            </div>
            <Button 
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-2 border-red-400"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Send New Message */}
          <Card className="border-4 border-red-400 shadow-2xl bg-white/90 backdrop-blur-sm h-fit">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-6 w-6 text-red-600" />
                Send New Message
              </CardTitle>
              <CardDescription>Ask a question or report a problem</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="subject" className="text-gray-700 font-semibold">
                    Subject <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="Brief subject of your message"
                    value={newMessage.subject}
                    onChange={handleChange}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-gray-700 font-semibold">
                    Message <span className="text-red-600">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Describe your problem or question in detail..."
                    value={newMessage.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12 text-lg font-bold text-white border-2 border-yellow-400 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)' }}
                >
                  {isLoading ? 'Sending...' : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Message History */}
          <Card className="border-4 border-blue-400 shadow-2xl bg-white/90 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="h-6 w-6 text-blue-600" />
                Your Messages ({messages.length})
              </CardTitle>
              <CardDescription>View your conversation history</CardDescription>
            </CardHeader>
            <CardContent>
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No messages yet</p>
                  <p className="text-sm text-gray-400">Send your first message to get help</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {messages.map((msg) => (
                    <Card key={msg._id || msg.id} className="border-2 hover:border-red-400 transition-colors cursor-pointer">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-bold text-gray-900">{msg.subject}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(msg.createdAt).toLocaleString('en-IN')}
                            </p>
                          </div>
                          {getStatusBadge(msg.status)}
                        </div>

                        <div className="space-y-3">
                          {/* User Message */}
                          <div className="p-3 rounded-lg bg-blue-50 border-l-4 border-blue-400">
                            <div className="flex justify-between items-start mb-1">
                              <p className="text-xs font-semibold text-gray-600">You</p>
                              <p className="text-xs text-gray-500">
                                {new Date(msg.createdAt).toLocaleTimeString('en-IN', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <p className="text-sm text-gray-700">{msg.message}</p>
                          </div>
                          
                          {/* Admin Reply */}
                          {msg.adminReply && (
                            <div className="p-3 rounded-lg bg-green-50 border-l-4 border-green-400">
                              <div className="flex justify-between items-start mb-1">
                                <p className="text-xs font-semibold text-gray-600">Admin Support</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(msg.updatedAt).toLocaleTimeString('en-IN', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <p className="text-sm text-gray-700">{msg.adminReply}</p>
                            </div>
                          )}
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Messages;
